// src/pages/Programs.js
import React, { useState } from 'react';
import { addDoc, collection, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useDataContext } from '../contexts/DataContext';

const approvalTypes = [
  { value: 'statewide', label: 'Statewide Approved' },
  { value: 'regional', label: 'Regional Approved' },
  { value: 'local', label: 'Local (Not Regionally Approved)' }
];

function getApprovalTypeLabel(approvalType) {
  return approvalTypes.find(t => t.value === approvalType)?.label || 'Unknown';
}

function Programs() {
  const { programs, uniqueCourseNames, loading, refreshData } = useDataContext();
  const [editingProgram, setEditingProgram] = useState(null);
  const [newProgram, setNewProgram] = useState({
    name: '',
    description: '',
    approvalType: 'statewide'
  });

  const handleCreateProgram = async (e) => {
    e.preventDefault();
    try {
      await addDoc(collection(db, 'programs'), {
        name: newProgram.name,
        description: newProgram.description,
        approvalType: newProgram.approvalType,
        courses: [],
        upperLevelCourses: []
      });
      setNewProgram({ name: '', description: '', approvalType: 'statewide' });
      refreshData();
    } catch (error) {
      console.error('Error creating program:', error);
    }
  };

  const handleEditProgram = (program) => {
    setEditingProgram({
      ...program,
      approvalType: program.approvalType || 'statewide'
    });
  };

  const handleUpdateProgram = async (e) => {
    e.preventDefault();
    if (!editingProgram) return;
    try {
      await updateDoc(doc(db, 'programs', editingProgram.id), {
        name: editingProgram.name,
        description: editingProgram.description,
        approvalType: editingProgram.approvalType,
        courses: editingProgram.courses || [],
        upperLevelCourses: editingProgram.upperLevelCourses || []
      });
      setEditingProgram(null);
      refreshData();
    } catch (error) {
      console.error('Error updating program:', error);
    }
  };

  const handleDeleteProgram = async (programId) => {
    if (!window.confirm('Are you sure you want to delete this program?')) return;
    try {
      await deleteDoc(doc(db, 'programs', programId));
      refreshData();
    } catch (error) {
      console.error('Error deleting program:', error);
    }
  };

  const handleAddCourseToProgram = (course) => {
    if (!editingProgram) return;
    const existing = editingProgram.courses || [];
    if (existing.includes(course)) return;
    setEditingProgram({ ...editingProgram, courses: [...existing, course] });
  };

  const handleRemoveCourseFromProgram = (course) => {
    if (!editingProgram) return;
    setEditingProgram({
      ...editingProgram,
      courses: (editingProgram.courses || []).filter(c => c !== course),
      upperLevelCourses: (editingProgram.upperLevelCourses || []).filter(c => c !== course)
    });
  };

  const handleToggleUpperLevel = (course) => {
    if (!editingProgram) return;
    const current = editingProgram.upperLevelCourses || [];
    setEditingProgram({
      ...editingProgram,
      upperLevelCourses: current.includes(course)
        ? current.filter(c => c !== course)
        : [...current, course]
    });
  };

  if (loading) return <div>Loading programs and courses...</div>;

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Programs of Study</h1>

      <div className="mb-8 p-4 border rounded bg-gray-50">
        <h2 className="text-xl font-semibold mb-4">Create New Program</h2>
        <form onSubmit={handleCreateProgram}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block mb-1">Program Name</label>
              <input
                type="text"
                value={newProgram.name}
                onChange={(e) => setNewProgram({ ...newProgram, name: e.target.value })}
                className="w-full border rounded px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="block mb-1">Description</label>
              <input
                type="text"
                value={newProgram.description}
                onChange={(e) => setNewProgram({ ...newProgram, description: e.target.value })}
                className="w-full border rounded px-3 py-2"
              />
            </div>
            <div>
              <label className="block mb-1">Approval Type</label>
              <select
                value={newProgram.approvalType}
                onChange={(e) => setNewProgram({ ...newProgram, approvalType: e.target.value })}
                className="w-full border rounded px-3 py-2"
              >
                {approvalTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
          </div>
          <button type="submit" className="mt-4 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700">
            Create Program
          </button>
        </form>
      </div>

      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-4">Existing Programs</h2>
        {programs.length === 0 ? (
          <p>No programs created yet. Create your first program above.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {programs.map(program => (
              <div key={program.id} className="border rounded p-4 bg-white">
                <h3 className="text-lg font-semibold">{program.name}</h3>
                <p className="text-gray-600 mb-2">{program.description}</p>
                <p className="text-sm mb-1">
                  <span className="font-semibold">Approval Type:</span> {getApprovalTypeLabel(program.approvalType || 'statewide')}
                </p>
                <p className="text-sm mb-1">
                  <span className="font-semibold">Courses:</span> {program.courses?.length || 0}
                </p>
                <p className="text-sm mb-3">
                  <span className="font-semibold">Upper Level Courses:</span> {program.upperLevelCourses?.length || 0}
                </p>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEditProgram(program)}
                    className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteProgram(program.id)}
                    className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editingProgram && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-screen overflow-y-auto">
            <h2 className="text-xl font-semibold mb-4">Edit Program: {editingProgram.name}</h2>
            <form onSubmit={handleUpdateProgram}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block mb-1">Program Name</label>
                  <input
                    type="text"
                    value={editingProgram.name}
                    onChange={(e) => setEditingProgram({ ...editingProgram, name: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                    required
                  />
                </div>
                <div>
                  <label className="block mb-1">Description</label>
                  <input
                    type="text"
                    value={editingProgram.description}
                    onChange={(e) => setEditingProgram({ ...editingProgram, description: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block mb-1">Approval Type</label>
                  <select
                    value={editingProgram.approvalType || 'statewide'}
                    onChange={(e) => setEditingProgram({ ...editingProgram, approvalType: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                  >
                    {approvalTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold mb-2">Available Courses</h3>
                  <div className="border rounded h-64 overflow-y-auto p-2">
                    {uniqueCourseNames.length === 0 ? (
                      <p className="text-gray-500">No courses available. Upload student data first.</p>
                    ) : (
                      <ul>
                        {uniqueCourseNames
                          .filter(course => !editingProgram.courses?.includes(course))
                          .map(course => (
                            <li
                              key={course}
                              className="py-1 px-2 hover:bg-gray-100 cursor-pointer flex justify-between items-center"
                            >
                              <span>{course}</span>
                              <button
                                type="button"
                                onClick={() => handleAddCourseToProgram(course)}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                Add →
                              </button>
                            </li>
                          ))}
                      </ul>
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Program Courses</h3>
                  <p className="text-sm mb-2">Check the box to mark as Level 3/4 (Upper Level)</p>
                  <div className="border rounded h-64 overflow-y-auto p-2">
                    {!editingProgram.courses || editingProgram.courses.length === 0 ? (
                      <p className="text-gray-500">No courses added to this program yet.</p>
                    ) : (
                      <ul>
                        {editingProgram.courses.map(course => (
                          <li
                            key={course}
                            className="py-1 px-2 hover:bg-gray-100 flex justify-between items-center"
                          >
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                checked={editingProgram.upperLevelCourses?.includes(course) || false}
                                onChange={() => handleToggleUpperLevel(course)}
                                className="mr-2"
                              />
                              <span className={
                                editingProgram.upperLevelCourses?.includes(course) ? 'font-semibold' : ''
                              }>
                                {course}
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveCourseFromProgram(course)}
                              className="text-red-600 hover:text-red-800"
                            >
                              Remove
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setEditingProgram(null)}
                  className="px-4 py-2 border rounded hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Programs;
